import React from 'react';
import { Checkmark16Regular, Dismiss16Regular } from '@fluentui/react-icons';
import styles from '../irlObservations2026.module.css';
import { EditableDueDate, EditablePriority, FreshnessBadge, PriorityMark, RagMark, StatusBadge, requestStatusLabel } from '../chrome';

export function DetailHeader({
    kind,
    item,
    onBack,
    onOpenAi,
    onCopy,
    copied,
    onEdit,
    extraActions,
    onUpdateFields,
    busy,
}) {
    const isIrl = kind === 'irl';
    const [menuOpen, setMenuOpen] = React.useState(false);
    return (
        <div className={styles.detailHeader}>
            <button type="button" className={styles.backBtn} onClick={onBack}>
                ← {isIrl ? 'Information Requests' : 'Observations'}
            </button>
            <div className={styles.detailTop}>
                <div>
                    <div className={styles.detailId}>{item.id}</div>
                    <h2 className={styles.detailTitle}>{stripHtmlText(item.title)}</h2>
                    <div className={styles.detailMeta}>
                        <span className={styles.metaPlain}>{item.workstreamName || item.workstream}</span>
                        {isIrl ? (
                            <EditablePriority item={item} onSave={onUpdateFields} busy={busy} />
                        ) : (
                            <>
                                <PriorityMark value={item.materiality} />
                                <RagMark value={observationRagLabel(item)} />
                            </>
                        )}
                        <StatusBadge status={isIrl ? requestStatusLabel(item) : item.status} />
                        <FreshnessBadge value={item.freshness} />
                        {isIrl ? (
                            <EditableDueDate item={item} appearance="header" onSave={onUpdateFields} busy={busy} />
                        ) : (
                            <span className={styles.metaPlain}>Updated {item.updated}</span>
                        )}
                    </div>
                </div>
                <div className={styles.headerActions}>
                    {onOpenAi && (
                        <button type="button" className={styles.ghostBtn} onClick={onOpenAi}>
                            AI Review
                        </button>
                    )}
                    {onCopy && (
                        <button type="button" className={styles.outlineBtn} onClick={onCopy}>
                            {copied ? 'Copied' : 'Copy link'}
                        </button>
                    )}
                    {onEdit && (
                        <button type="button" className={styles.outlineBtn} onClick={onEdit}>
                            Edit
                        </button>
                    )}
                    {extraActions && (
                        <div className={styles.menu}>
                            <button
                                type="button"
                                className={styles.iconBtn}
                                onClick={() => setMenuOpen(open => !open)}
                                aria-label="More">
                                ···
                            </button>
                            {menuOpen && (
                                <div className={styles.menuPop} onClick={() => setMenuOpen(false)}>
                                    {extraActions}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export function DetailTabs({ tabs, tab, onTab, counts }) {
    return (
        <div className={styles.tabs}>
            {tabs.map(name => {
                const count = counts?.[name];
                return (
                    <button key={name} type="button" className={`${styles.tab} ${tab === name ? styles.tabActive : ''}`} onClick={() => onTab(name)}>
                        {name}
                        {typeof count === 'number' && count > 0 && <span className={name === 'Discussion' ? styles.tabCount : styles.tabMutedCount}>{count}</span>}
                    </button>
                );
            })}
        </div>
    );
}

export function EvidenceUploadButton({ onUpload, disabled }) {
    const inputRef = React.useRef(null);
    return (
        <>
            <button type="button" className={styles.outlineBtn} onClick={() => inputRef.current?.click()} disabled={disabled}>
                Upload evidence
            </button>
            <input
                ref={inputRef}
                type="file"
                hidden
                multiple
                disabled={disabled}
                onChange={event => {
                    onUpload?.(event.target.files);
                    event.target.value = '';
                }}
            />
        </>
    );
}

export function UploadingEvidenceStatus() {
    return (
        <div className={styles.progressStep} role="status" aria-live="polite">
            <span className={styles.stepActive}>
                <span className={styles.stepActiveDot} />
            </span>
            Uploading evidence
        </div>
    );
}

export function ResponseComposer({ value, onChange, onSubmit, onSaveDraft, onCancel, busy, extraAction }) {
    return (
        <div className={styles.fieldBlock}>
            <label className={styles.fieldLabel}>Response</label>
            <textarea className={styles.textarea} value={value} onChange={event => onChange(event.target.value)} placeholder="Write the response" />
            <div className={styles.emptyActions}>
                {onCancel && (
                    <button type="button" className={styles.mutedBtn} onClick={onCancel} disabled={busy}>
                        Cancel
                    </button>
                )}
                {onSaveDraft && (
                    <button type="button" className={styles.outlineBtn} onClick={onSaveDraft} disabled={busy || !value.trim()}>
                        Save draft
                    </button>
                )}
                <button type="button" className={styles.primaryBtn} onClick={onSubmit} disabled={busy || !value.trim()}>
                    Submit response
                </button>
                {extraAction}
            </div>
        </div>
    );
}

export function evidenceDocumentId(file) {
    return file?.documentId ?? file?.document_id ?? null;
}

export function evidenceIncludesUploads(request, { beforeIds, beforeCount, pendingNames }) {
    const files = request?.evidence || [];
    const names = pendingNames || [];
    const knownIds = beforeIds || new Set();
    const newFiles = files.filter(file => {
        const id = evidenceDocumentId(file);
        if (id) return !knownIds.has(id);
        return names.includes(file.name);
    });
    if (names.length && newFiles.length >= names.length) return true;
    return files.length >= (beforeCount || 0) + names.length;
}

export function canOpenEvidenceFile(file) {
    return Boolean(evidenceDocumentId(file) || file?.openUrl);
}

export function FileRow({ file, onOpen }) {
    return (
        <div className={styles.fileRow}>
            <div className={styles.fileLeft}>
                <span className={styles.pdfBadge}>PDF</span>
                <div>
                    <div className={styles.fileName}>{file.name}</div>
                    <div className={styles.filePath}>{file.path}{file.date ? ` · ${file.date}` : ''}{file.page ? ` · ${file.page}` : ''}</div>
                </div>
            </div>
            <div className={styles.fileActions}>
                {file.ready === false ? (
                    <span className={styles.processingDot}>Processing</span>
                ) : (
                    <span className={styles.readyDot}>
                        <span className={`${styles.dot} ${styles.readyOk}`} />
                        Ready
                    </span>
                )}
                <button type="button" className={styles.alertLink} onClick={() => onOpen?.(file)} disabled={!canOpenEvidenceFile(file)}>
                    Open
                </button>
            </div>
        </div>
    );
}

export function Confidence({ value }) {
    const label = String(value || '').trim() || 'Low';
    const key = label.toLowerCase();
    const cls = key === 'high' ? styles.confidenceHigh : key === 'medium' ? styles.confidenceMedium : styles.confidenceLow;
    return <span className={cls}>Confidence: {label}</span>;
}

export function AiReviewReadyBanner({ onReview }) {
    return (
        <div className={styles.readyBanner}>
            <p className={styles.readyBannerTitle}>AI Review ready</p>
            <p className={styles.readyBannerBody}>Suggestions are waiting for you.</p>
            <button type="button" className={styles.readyBannerLink} onClick={onReview}>
                Review suggestions
            </button>
        </div>
    );
}

export function SuggestHeader({ onBack, title = 'Review AI Suggestions', meta }) {
    const dateLabel = meta?.createdAt || '';
    const scopeLabel = meta?.scopeLabel || meta?.scope?.label || '';
    const summary = ['AI Review', dateLabel, scopeLabel].filter(Boolean).join(' · ');
    const evidenceBanner = meta?.newEvidenceBanner || '';
    return (
        <div>
            <div className={styles.suggestHead}>
                <div>
                    <button type="button" className={styles.backBtn} onClick={onBack}>
                        ← Information Requests
                    </button>
                    <h2 className={styles.suggestKicker}>{title}</h2>
                    <p className={styles.suggestMeta}>{summary || 'AI Review'}</p>
                </div>
                {meta?.limitations ? <span className={styles.suggestBanner}>{meta.limitations}</span> : null}
            </div>
            {evidenceBanner ? (
                <div className={styles.alert}>
                    <p className={styles.alertTitle}>{evidenceBanner}</p>
                </div>
            ) : null}
        </div>
    );
}

export function SuggestTabs({ tabs, tab, onTab, counts }) {
    const resolved = {
        Responses: counts?.proposed_responses ?? counts?.Responses ?? 0,
        Observations: counts?.proposed_observations ?? counts?.Observations ?? 0,
        'Follow-up IRLs': counts?.follow_ups ?? counts?.['Follow-up IRLs'] ?? 0,
        Contradictions: counts?.contradictions ?? counts?.Contradictions ?? 0,
    };
    return (
        <div className={styles.tabs}>
            {tabs.map(name => (
                <button key={name} type="button" className={`${styles.tab} ${tab === name ? styles.tabActive : ''}`} onClick={() => onTab(name)}>
                    {name}
                    {resolved[name] ? <span className={styles.tabMutedCount}>{resolved[name]}</span> : null}
                </button>
            ))}
        </div>
    );
}

export function MetaPair({ label, children }) {
    return (
        <div className={styles.metaPair}>
            <div className={styles.metaLabel}>{label}</div>
            <div className={styles.metaValue}>{children}</div>
        </div>
    );
}

export function IrlCounts(request) {
    return {
        Evidence: request.evidence?.length || request.evidenceCount || 0,
        Related: request.linkedObservations?.length || 0,
        Discussion: request.discussionCount || request.discussion?.length || 0,
    };
}

const RAG_FROM_MATERIALITY = {
    High: 'Red',
    Medium: 'Amber',
    Low: 'Green',
};

const RAG_DISPLAY = {
    RED: 'Red',
    AMBER: 'Amber',
    GREEN: 'Green',
    Red: 'Red',
    Amber: 'Amber',
    Green: 'Green',
};

export function observationRagLabel(item, { fallback = false } = {}) {
    const raw = String(item?.rag || '').trim();
    const mapped = RAG_DISPLAY[raw] || RAG_DISPLAY[raw.toUpperCase()] || '';
    if (mapped) return mapped;
    if (!fallback) return '';
    return RAG_FROM_MATERIALITY[item?.materiality] || '';
}

export function ObsCounts(observation) {
    return {
        Evidence: observation.evidence?.length || 0,
        Related: observation.relatedIrls?.length || 0,
        Discussion: observation.discussionCount || observation.discussion?.length || 0,
    };
}

const HTML_ENTITIES = {
    amp: '&',
    lt: '<',
    gt: '>',
    quot: '"',
    apos: "'",
    nbsp: ' ',
};

function decodeHtmlEntities(value) {
    return String(value || '')
        .replace(/&nbsp;/gi, ' ')
        .replace(/&#x([0-9a-f]+);/gi, (_, hex) => String.fromCharCode(parseInt(hex, 16)))
        .replace(/&#(\d+);/g, (_, num) => String.fromCharCode(Number(num)))
        .replace(/&(#39|apos);/gi, "'")
        .replace(/&(amp|lt|gt|quot);/gi, (match, name) => HTML_ENTITIES[name.toLowerCase()] || match);
}

export function stripHtmlText(value) {
    if (value == null || value === '') return '';
    const decoded = decodeHtmlEntities(String(value));
    return decodeHtmlEntities(decoded.replace(/<[^>]*>/g, ' '))
        .replace(/\s+/g, ' ')
        .replace(/\s+([,.;:!?])/g, '$1')
        .trim();
}

function plainRelatedCards(items) {
    return (items || []).map(item => ({ ...item, title: stripHtmlText(item.title) }));
}

export function withPlainObservationText(item) {
    if (!item || typeof item !== 'object') return item;
    return {
        ...item,
        title: stripHtmlText(item.title),
        body: item.body != null ? stripHtmlText(item.body) : item.body,
        why: item.why != null ? stripHtmlText(item.why) : item.why,
        relatedIrls: item.relatedIrls ? plainRelatedCards(item.relatedIrls) : item.relatedIrls,
        linkedObservations: item.linkedObservations ? plainRelatedCards(item.linkedObservations) : item.linkedObservations,
    };
}

function plainResponse(response) {
    if (!response || typeof response !== 'object') return response;
    return {
        ...response,
        body: response.body != null ? stripHtmlText(response.body) : response.body,
        response_text: response.response_text != null ? stripHtmlText(response.response_text) : response.response_text,
    };
}

export function withPlainRequestText(item) {
    if (!item || typeof item !== 'object') return item;
    return {
        ...withPlainLinkedObservations(item),
        title: stripHtmlText(item.title),
        tag: item.tag != null ? stripHtmlText(item.tag) : item.tag,
        context: item.context != null ? stripHtmlText(item.context) : item.context,
        response: plainResponse(item.response),
        followUps: item.followUps ? plainRelatedCards(item.followUps) : item.followUps,
    };
}

export function withPlainLinkedObservations(item) {
    if (!item?.linkedObservations) return item;
    return { ...item, linkedObservations: plainRelatedCards(item.linkedObservations) };
}

export function suggestionCardKey(item) {
    return item?.proposalId || item?.proposal_id || item?.id || item?.irlId || item?.parentId || item?.title || item?.left;
}

export function SuggestionEdit({ value, onChange, onSave, onCancel, disabled }) {
    return (
        <>
            <textarea className={styles.textarea} value={value} onChange={event => onChange(event.target.value)} />
            <div className={styles.editActions}>
                <button
                    type="button"
                    className={styles.editSaveBtn}
                    onClick={onSave}
                    disabled={disabled || !String(value || '').trim()}
                    aria-label="Accept edit">
                    <Checkmark16Regular />
                </button>
                <button type="button" className={styles.editCancelBtn} onClick={onCancel} aria-label="Cancel edit">
                    <Dismiss16Regular />
                </button>
            </div>
        </>
    );
}

function escapeRegExp(value) {
    return String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export function mentionIdsFromText(text, candidates) {
    const body = String(text || '');
    const ids = [];
    [...(candidates || [])]
        .filter(member => member?.name)
        .sort((left, right) => String(right.name).length - String(left.name).length)
        .forEach(member => {
            const pattern = new RegExp(`@${escapeRegExp(member.name)}(?=$|[\\s.,!?;:)'"])`, 'i');
            if (pattern.test(body) && !ids.includes(member.id)) ids.push(member.id);
        });
    return ids;
}
